/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  useTheme,
  Box,
  Stack,
  alpha,
  Typography,
  IconButton,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import {
  InventoryEstimate,
  InventoryEstimateItem,
  SupportedLanguage,
} from "src/store/api/generatedApi";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useLanguage } from "hooks/settings";
import { InventoryMeasurementSubmitButton } from "./InventoryMeasurementSubmitButton";
import { useInventoryMeasurement } from "./InventoryMeasurementProvider";
import { InventoryMeasurementFormState } from "../types";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { NumberCell } from "src/components/common/dataGrid/NumberCell";
import { useNotifyStatus } from "contexts/status";
import { useFormatNumber } from "src/utils/formatNumber";
import { MinimumExceededError, parseNumber } from "src/utils/parseNumber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useState } from "react";
import { InventoryEstimateModal } from "./InventoryEstimateModal";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";

export type InventoryMeasurementRow = InventoryEstimateItem & {
  adjusted_quantity: number | null;
};

export interface InventoryMeasurementFormDesktopProps {
  inventoryEstimate: InventoryEstimate;
}

const EstimatedInventoryHeader = ({
  unitsFormatter,
  onInfoClick,
}: {
  unitsFormatter: ReturnType<typeof useUnitsFormatter>;
  onInfoClick: () => void;
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography
        variant="body2"
        color={theme.palette.text.secondary}
        sx={{ flexGrow: 1 }}
      >
        {`${t("estimatedInventory")}${unitsFormatter("mass")}`}
      </Typography>
      <IconButton size="small" onClick={onInfoClick} sx={{ p: 0.25 }}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const InventoryHeader = ({
  unitsFormatter,
}: {
  unitsFormatter: ReturnType<typeof useUnitsFormatter>;
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  return (
    <Typography variant="body2" color={theme.palette.text.secondary}>
      {`${t("inventory")}${unitsFormatter("mass")}`}
    </Typography>
  );
};

export const InventoryMeasurementFormDesktop = ({
  inventoryEstimate,
}: InventoryMeasurementFormDesktopProps) => {
  const { t } = useTenantTranslation();
  const unitsFormatter = useUnitsFormatter(true);
  const theme = useTheme();
  const language = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isEstimateMode = useIsBehindFeatureFlag(
    "inventory_measurement_with_estimate"
  );

  const {
    inventoryMeasurementFormData,
    adjustQuantity,
    inventoryMeasurementFormState,
  } = useInventoryMeasurement();

  const notifyStatus = useNotifyStatus();

  const parseNumberLocalised = parseNumber(language as SupportedLanguage);
  const formatNumber = useFormatNumber();
  const parseNumber2DP = parseNumberLocalised({ decimalPlaces: 2, minimum: 0 });
  const formatNumber2DP = formatNumber(2);

  // Filter items with non-null specific_price and add adjusted_quantity
  const rows: InventoryMeasurementRow[] = inventoryEstimate.items
    .filter((item) => item.specific_price !== null)
    .map((item) => {
      const adjustedQuantity =
        inventoryMeasurementFormData.items.find(
          (i) => i.material_name === item.material_name
        )?.adjusted_quantity ?? null;
      return { ...item, adjusted_quantity: adjustedQuantity };
    });

  const handleRowUpdate = (
    updatedRow: InventoryMeasurementRow
  ): InventoryMeasurementRow => {
    adjustQuantity(updatedRow.material_name, updatedRow.adjusted_quantity);
    return updatedRow;
  };

  const columns: GridColDef<InventoryMeasurementRow>[] = [
    {
      field: "material_name",
      headerName: t("material"),
      sortable: false,
      flex: 1.5,
      display: "flex",
    },
    {
      field: "quantity",
      headerName: isEstimateMode
        ? `${t("estimatedInventory")}${unitsFormatter("mass")}`
        : `${t("inventory")}${unitsFormatter("mass")}`,
      flex: 1,
      sortable: false,
      renderHeader: () =>
        isEstimateMode ? (
          <EstimatedInventoryHeader
            unitsFormatter={unitsFormatter}
            onInfoClick={() => {
              setIsModalOpen(true);
            }}
          />
        ) : (
          <InventoryHeader unitsFormatter={unitsFormatter} />
        ),
      renderCell: (params) => (
        <NumberCell decimalPlaces={2} value={params.row.quantity ?? null} />
      ),
      display: "flex",
    },
    {
      field: "specific_price",
      headerName: `${t("price")}${unitsFormatter("specific_cost")}`,
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <NumberCell
          decimalPlaces={2}
          value={params.row.specific_price ?? null}
        />
      ),
      display: "flex",
    },
    {
      field: "adjusted_quantity",
      headerName: `${t("actualInventory")}${unitsFormatter("mass")}`,
      sortable: false,
      editable: true,
      flex: 1,
      cellClassName: "numeric adjustment-input",
      valueSetter: (value: string, row) => {
        try {
          if (value) {
            const adjusted_quantity = parseNumber2DP(value);
            return {
              ...row,
              adjusted_quantity,
            };
          } else {
            return {
              ...row,
              adjusted_quantity: null,
            };
          }
        } catch (e) {
          switch (true) {
            case e instanceof MinimumExceededError:
              notifyStatus({ text: t("invalidNumber"), type: "error" });
          }
          return row;
        }
      },
      valueGetter: (_: unknown, row) => {
        if (
          row.adjusted_quantity !== null &&
          row.adjusted_quantity !== undefined
        ) {
          return formatNumber2DP(row.adjusted_quantity);
        } else {
          return null;
        }
      },
      display: "flex",
    },
  ];

  return (
    <>
      <Stack maxHeight="100%" sx={{ mb: 3, maxWidth: "50vw", mx: "auto" }}>
        <HotkeyCompliantDataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.material_name}
          processRowUpdate={handleRowUpdate}
          isCellEditable={() =>
            inventoryMeasurementFormState !==
            InventoryMeasurementFormState.Submitting
          }
          initialState={{
            sorting: {
              sortModel: [{ field: "material_name", sort: "asc" }],
            },
          }}
          sx={{
            maxHeight: `100%`,
            "& .MuiDataGrid-cell--editing input": {
              textAlign: "right",
              paddingX: 1,
              ...theme.typography.body1Mono,
            },
            ".numeric": {
              display: "flex",
              justifyContent: "flex-end",
              ...theme.typography.body1Mono,
            },
            ".adjustment-input": {
              backgroundColor: alpha(theme.palette.data.blue, 0.1),
            },
          }}
          cellSelection
          ignoreValueFormatterDuringExport
        />
      </Stack>
      <Box sx={{ textAlign: "right" }}>
        <InventoryMeasurementSubmitButton />
      </Box>
      <InventoryEstimateModal
        open={isModalOpen}
        rows={rows}
        latestDeliveryDataTimestamp={
          inventoryEstimate.latest_delivery_data_timestamp
        }
        lastInventoryReportTimestamp={
          inventoryEstimate.last_inventory_report_timestamp
        }
        onClose={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
};
